// The browser client for a saved App's function runs: the newest first, and one run's log, input
// and error for its editors. GET /api/v1/company/apps/runs/:artifactId/:versionId[/:runId] over the
// session-bound fetch. It reads only; a 403 means this person is not an editor, so the section hides.
export type RunOutcome = 'succeeded' | 'failed' | 'unknown';
export type RunTrigger = 'manual' | 'schedule' | 'webhook' | 'event';
export type RunSummary = {
  id: string;
  functionId: string;
  trigger: RunTrigger;
  jobId: string | null;
  outcome: RunOutcome;
  code: string | null;
  finishedAt: string;
  durationMs: number | null;
  versionId: string;
};
export type RunLogLine = { level: string; text: string; at: number | null };
export type RunDetail = RunSummary & {
  input: unknown;
  logs: { lines: RunLogLine[]; dropped: number };
  error: { name: string; message: string; stack: string[] } | null;
};
export type RunsPage = { runs: RunSummary[]; nextCursor: string | null };

/** This person cannot read the App's runs (not an editor). */
export class RunsNotAvailableToViewer extends Error {
  constructor() {
    super('app_runs_not_authorized');
  }
}

export interface AppRunsApi {
  list(cursor?: string | null): Promise<RunsPage>;
  detail(runId: string): Promise<RunDetail>;
}

const PREFIX = '/api/v1/company/apps/runs';
const OUTCOMES: readonly RunOutcome[] = ['succeeded', 'failed', 'unknown'];
const TRIGGERS: readonly RunTrigger[] = ['manual', 'schedule', 'webhook', 'event'];
const obj = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
const text = (value: unknown): value is string => typeof value === 'string' && value.length > 0;
const nullableText = (value: unknown) => (value === null || text(value) ? value : undefined);

export function parseRunSummary(value: unknown): RunSummary | null {
  const raw = obj(value);
  if (
    !raw ||
    !text(raw.id) ||
    !text(raw.functionId) ||
    !text(raw.versionId) ||
    !text(raw.finishedAt) ||
    !Number.isFinite(Date.parse(raw.finishedAt))
  )
    return null;
  const trigger = TRIGGERS.find((item) => item === raw.trigger);
  const outcome = OUTCOMES.find((item) => item === raw.outcome);
  const jobId = nullableText(raw.jobId ?? null);
  const code = nullableText(raw.code ?? null);
  if (!trigger || !outcome || jobId === undefined || code === undefined) return null;
  const duration = raw.durationMs;
  return {
    id: raw.id,
    functionId: raw.functionId,
    trigger,
    jobId: jobId as string | null,
    outcome,
    code: code as string | null,
    finishedAt: raw.finishedAt,
    durationMs:
      Number.isSafeInteger(duration) && (duration as number) >= 0 ? (duration as number) : null,
    versionId: raw.versionId,
  };
}

function parseLogs(value: unknown): RunDetail['logs'] {
  const raw = obj(value);
  const lines = Array.isArray(raw?.lines)
    ? raw.lines.flatMap((entry) => {
        const line = obj(entry);
        return line && typeof line.text === 'string'
          ? [
              {
                level: typeof line.level === 'string' ? line.level : 'log',
                text: line.text,
                at: Number.isSafeInteger(line.at) ? (line.at as number) : null,
              },
            ]
          : [];
      })
    : [];
  const dropped = Number.isSafeInteger(raw?.dropped) ? (raw?.dropped as number) : 0;
  return { lines, dropped };
}

function parseError(value: unknown): RunDetail['error'] {
  const raw = obj(value);
  if (!raw) return null;
  return {
    name: typeof raw.name === 'string' ? raw.name : 'Error',
    message: typeof raw.message === 'string' ? raw.message : '',
    stack: Array.isArray(raw.stack)
      ? raw.stack.filter((line): line is string => typeof line === 'string')
      : [],
  };
}

export function parseRunDetail(value: unknown): RunDetail | null {
  const summary = parseRunSummary(value);
  const raw = obj(value);
  if (!summary || !raw) return null;
  return {
    ...summary,
    input: raw.input ?? null,
    logs: parseLogs(raw.logs),
    error: parseError(raw.error),
  };
}

export function createAppRunsApi(
  artifactId: string,
  versionId: string,
  send: typeof fetch,
): AppRunsApi {
  const base = `${PREFIX}/${encodeURIComponent(artifactId)}/${encodeURIComponent(versionId)}`;
  const read = async (url: string) => {
    const response = await send(url, { credentials: 'include' });
    if (response.status === 403) throw new RunsNotAvailableToViewer();
    if (response.status !== 200) throw new Error('app_runs_unavailable');
    return response.json();
  };
  return {
    async list(cursor) {
      const body = obj(await read(cursor ? `${base}?${new URLSearchParams({ cursor })}` : base));
      if (!body || !Array.isArray(body.runs)) throw new Error('app_runs_unreadable');
      const runs = body.runs.map(parseRunSummary);
      if (runs.some((run) => !run)) throw new Error('app_runs_unreadable');
      const next = body.nextCursor;
      if (next !== null && !text(next)) throw new Error('app_runs_unreadable');
      return { runs: runs as RunSummary[], nextCursor: next as string | null };
    },
    async detail(runId) {
      const detail = parseRunDetail(await read(`${base}/${encodeURIComponent(runId)}`));
      if (!detail || detail.id !== runId) throw new Error('app_run_unreadable');
      return detail;
    },
  };
}
